import { I18n } from '@calendly/i18n';
import { FixedContainer } from '@calendly/ui/components/fixed-container';
import { ArrowDownIcon } from '@calendly/ui/components/icons';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { useCallback, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { useProductRefreshAppShell } from 'features/app_shell/product_refresh/useProductRefreshAppShell';
import { modalVisibleSelector } from 'features/book_modal';
import { trackContactInteraction } from 'features/contacts';
import { isShareOrReschedulePopupOpenSelector } from 'features/host_reschedule';
import { useTrack } from 'hooks/analytics_events';
import { isFilterModalOpenSelector } from 'selectors/meetings/filter_modal';
import { scrollToToday } from 'shared/utils';
import { ArrowUpIcon, StyledButton } from './styles';
import { useScrollToTodayButton } from './use_scroll_to_today_button';
const buttonContainerIsTopCss = "b1gthv1t";
const buttonContainerBehindFilterCss = "bmgmve9";
const buttonContainerIsProductRefreshAppShellCss = "bswu6mo";
const buttonContainerCss = "b1tjwhet";
const relativeButtonContainerCss = "r10rtq5k";
const ButtonContainer = forwardRef((props, ref) => {
  const {
    isTop,
    useRelativePositioning = false,
    isBehindFilter = false,
    ...restProps
  } = props;
  const Container = useRelativePositioning ? 'div' : FixedContainer;
  const isProductRefreshAppShell = useProductRefreshAppShell();
  return <Container ref={ref} {...restProps} className={classNames(buttonContainerCss, {
    [buttonContainerIsTopCss]: isTop,
    [buttonContainerIsProductRefreshAppShellCss]: isProductRefreshAppShell,
    [buttonContainerBehindFilterCss]: isBehindFilter,
    [relativeButtonContainerCss]: useRelativePositioning
  }, props.className)} />;
});
const buttonContentCss = "bnx5kz";
const ButtonContent = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(buttonContentCss, props.className)} />;
});

/**
 * Presentational ScrollToTodayButton component.
 * This is a "dumb" component that receives all state and callbacks as props,
 * making it easy to test and use in Storybook.
 *
 * @param {Object} props
 * @param {boolean} props.isVisible - Whether the button should be visible
 * @param {boolean} props.isTop - Whether the button should be positioned at the top
 * @param {number|undefined} props.buttonLeft - Horizontal position of the button (in pixels)
 * @param {Function} props.onClick - Callback when the button is clicked
 * @param {boolean} [props.useRelativePositioning] - Use relative positioning instead of fixed (for Storybook)
 * @param {boolean} [props.isBehindFilter] - Whether the button should appear behind the filter modal
 * @param {string} [props.className] - Optional additional className
 */
export const ScrollToTodayButtonPresenter = ({
  isVisible,
  isTop,
  buttonLeft,
  onClick,
  useRelativePositioning = false,
  isBehindFilter = false,
  className,
  ...props
}) => {
  if (!isVisible) return null;
  const buttonContainerLeftCss = "b15wyww7";
  return <ButtonContainer isTop={isTop} useRelativePositioning={useRelativePositioning} isBehindFilter={isBehindFilter} style={buttonLeft !== undefined ? {
    left: buttonLeft
  } : undefined} className={classNames({
    [buttonContainerLeftCss]: buttonLeft !== undefined
  }, className)}>
      <StyledButton onClick={onClick} decoration="secondary-outline" {...props}>
        <ButtonContent>
          {isTop ? <>
              <ArrowUpIcon />
              {I18n.t('publisher.meetings.scroll_to_today')}
            </> : <>
              <ArrowDownIcon />
              {I18n.t('publisher.meetings.scroll_to_today')}
            </>}
        </ButtonContent>
      </StyledButton>
    </ButtonContainer>;
};

/**
 * Smart wrapper component that provides the default behavior.
 * This component uses the hook and handles scroll logic and analytics.
 * This is the default export for backward compatibility.
 * For Storybook, use ScrollToTodayButtonPresenter with the hook directly.
 *
 * @param {Object} props
 * @param {React.RefObject<HTMLDivElement>} props.containerRef - Ref to the scrollable container
 * @param {Array<{rawDate: string, date: string, events?: unknown[]}>} props.meetings - Array of meeting groups
 * @param {string} props.timezone - Timezone string (e.g., 'America/New_York')
 * @param {string|null} [props.contactUuid] - Optional contact UUID for analytics tracking
 * @param {moment.Moment} [props.currentTime] - Optional current time moment object for timezone-aware scrolling
 */
export const ScrollToTodayButton = ({
  containerRef,
  meetings,
  timezone,
  contactUuid = null,
  currentTime = null,
  ...props
}) => {
  const track = useTrack();
  const isFilterModalOpen = useSelector(isFilterModalOpenSelector);
  const isBookModalOpen = useSelector(modalVisibleSelector);
  const isReschedulePopupOpen = useSelector(isShareOrReschedulePopupOpenSelector);
  const {
    buttonLeft,
    isTop,
    isVisible
  } = useScrollToTodayButton({
    containerRef,
    meetings,
    timezone,
    currentTime
  });
  const handleScrollToToday = useCallback(() => {
    const now = currentTime || moment().tz(timezone);
    scrollToToday(containerRef, meetings, timezone, true, now);

    // Track from Contact page if contactUuid provided
    if (contactUuid) {
      trackContactInteraction({
        track,
        location: 'interactions_feed_all',
        action: 'scroll_to_today',
        contactUuid
      });
    }
  }, [containerRef, meetings, timezone, currentTime, contactUuid, track]);
  return <ScrollToTodayButtonPresenter isVisible={isVisible} isTop={isTop} buttonLeft={buttonLeft} onClick={handleScrollToToday} isBehindFilter={isFilterModalOpen || isBookModalOpen || isReschedulePopupOpen} {...props} />;
};

require("./scroll_to_today_button.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./scroll_to_today_button.js");